<template>
  <div>
    <b-overlay :show="loading" rounded="sm">
      <b-card no-body>
        <b-table-simple hover responsive class="mb-0">
          
          <b-thead head-variant="light">
            <b-tr>
              <b-th>#</b-th>
              <b-th>Collection</b-th>
              <b-th>Records</b-th>
              <b-th>Storage (MB)</b-th>
              <b-th>Actions</b-th>
            </b-tr>
          </b-thead>

          <b-tbody>
            <b-tr v-for="(item, index) in collections" :key="index">
              <b-td>{{ index + 1 }}</b-td>
              <b-td>{{ item.collection }}</b-td>
              <b-td>{{ item.count }}</b-td>
              <b-td>{{ item.storageMB }}</b-td>
              <b-td>
                <!-- Action Dropdown -->
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  right
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      class="cursor-pointer text-dark"
                    />
                  </template>

                  <b-dropdown-item @click="openDeleteModal(item)">
                    <feather-icon icon="TrashIcon" class="mr-50" />
                    <span>Delete Records</span>
                  </b-dropdown-item>
                </b-dropdown>
              </b-td>
            </b-tr>

            <b-tr v-if="!collections.length && !loading">
              <b-td colspan="5" class="text-center text-muted">
                No data found
              </b-td>
            </b-tr>
          </b-tbody>

        </b-table-simple>
      </b-card>
    </b-overlay>

<!-- Second Table - All Collections -->
<b-overlay :show="allCollectionsLoading" rounded="sm">
  <b-card no-body>
    <b-card-header header-tag="header" class="bg-white d-flex justify-content-between align-items-center">
      <h4 class="mb-0">All Collections</h4>
      <b-button
        variant="outline-primary"
        size="sm"
        @click="fetchAllCollections"
        :disabled="allCollectionsLoading"
      >
        <feather-icon
          icon="RefreshCwIcon"
          class="mr-50"
          :class="{ 'spin': allCollectionsLoading }"
        />
        Refresh
      </b-button>
    </b-card-header>

    <b-table-simple hover responsive class="mb-0">
      
      <b-thead head-variant="light">
        <b-tr>
          <b-th>#</b-th>
          <b-th>Collection</b-th>
          <b-th>Records</b-th>
          <b-th>Storage (MB)</b-th>
        </b-tr>
      </b-thead>

      <b-tbody>
        <b-tr v-for="(item, index) in allCollections" :key="index">
          <b-td>{{ index + 1 }}</b-td>
          <b-td>{{ item.collection || item.name }}</b-td>
          <b-td>{{ formatNumber(item.count || item.documentCount || 0) }}</b-td>
          <b-td>{{ formatNumber(item.storageMB || item.storageSize || 0, 2) }}</b-td>
        </b-tr>

        <b-tr v-if="!allCollections.length && !allCollectionsLoading">
          <b-td colspan="4" class="text-center text-muted">
            No collections found
          </b-td>
        </b-tr>
      </b-tbody>

      <!-- Table Footer with Summary -->
      <b-tfoot v-if="allCollections.length" class="bg-light">
        <b-tr>
          <b-td colspan="2" class="font-weight-bold">Total</b-td>
          <b-td class="font-weight-bold">{{ formatNumber(totalDocuments) }}</b-td>
          <b-td class="font-weight-bold">{{ formatNumber(totalStorage, 2) }}</b-td>
        </b-tr>
      </b-tfoot>

    </b-table-simple>
  </b-card>
</b-overlay>

    <b-modal
      id="delete-records-modal"
      ref="deleteModal"
      title="Delete Records"
      title-tag="h4"
      size="lg"
      centered
      hide-footer
      @hidden="resetModal"
    >
      <div v-if="selectedCollection">
        <!-- Collection Info -->
        <b-alert variant="danger" show class="mb-3">
          <div class="alert-body">
            <feather-icon icon="AlertTriangleIcon" class="mr-50" />
            <strong>Warning:</strong> You are about to delete records from
            <strong>{{ selectedCollection.collection }}</strong>
          </div>
        </b-alert>

        <!-- Tabs for Duration Selection -->
        <b-tabs v-model="activeTab" content-class="mt-3">
          <!-- Tab 1: Static Durations -->
          <b-tab title="Quick Duration">
            <b-form-group
              label="Select Duration"
              label-for="duration-select"
              label-class="font-weight-bold"
              class="mb-4"
            >
              <b-form-select
                id="duration-select"
                v-model="selectedDuration"
                :options="staticDurationOptions"
                :disabled="fetchingCount"
                required
                @change="onDurationChange"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    Select a duration
                  </b-form-select-option>
                </template>
              </b-form-select>
              <b-form-text class="mt-1">
                Records older than the selected duration will be deleted
              </b-form-text>
            </b-form-group>
          </b-tab>

          <!-- Tab 2: Date Range -->
          <b-tab title="Custom Date Range">
            <b-form-group
              label="Select Date Range"
              label-for="date-range"
              label-class="font-weight-bold"
              class="mb-4"
            >
               <date-range-picker
                ref="picker"
                :opens="'left'"
                :locale-data="{
                  direction: 'ltr',
                  format: 'yyyy-mm-dd',
                  separator: ' - ',
                  applyLabel: 'Apply',
                  cancelLabel: 'Cancel',
                  weekLabel: 'W',
                  customRangeLabel: 'Custom Range',
                  daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                  monthNames: [
                    'Jan',
                    'Feb',
                    'Mar',
                    'Apr',
                    'May',
                    'Jun',
                    'Jul',
                    'Aug',
                    'Sep',
                    'Oct',
                    'Nov',
                    'Dec',
                  ],
                  firstDay: 0,
                }"
                :minDate="'2019-01-01 00:00:00'"
                :maxDate="'2099-01-01 00:00:00'"
                :timePicker="false"
                :timePicker24Hour="true"
                :showWeekNumbers="false"
                :showDropdowns="true"
                :autoApply="true"
                v-model="dateRange"
                @update="onDateRangeChange"
                :linkedCalendars="true">
              </date-range-picker>
              <b-form-text class="mt-1">
                Records within the selected date range will be deleted
              </b-form-text>
            </b-form-group>
          </b-tab>
        </b-tabs>

        <!-- Show loader while fetching count -->
        <div v-if="fetchingCount" class="text-center py-5">
          <b-spinner variant="primary" class="mb-3" small></b-spinner>
          <p class="text-muted mb-0">Fetching records count...</p>
        </div>

        <!-- Show content after count is fetched -->
        <div v-else-if="hasSelection">
          <template v-if="previewCount > 0">
            <!-- Record Count Preview -->
            <b-card class="mb-4">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">Records to be affected:</h6>
                  <small class="text-muted">
                    <template v-if="activeTab === 0">
                      {{ previewCount.toLocaleString() }} records older than {{ durationLabel }}
                    </template>
                    <template v-else>
                      {{ previewCount.toLocaleString() }} records between {{ formattedStartDate }} and {{ formattedEndDate }}
                    </template>
                  </small>
                </div>
                <b-badge variant="light-warning" class="px-2 py-1">
                  {{ previewCount.toLocaleString() }} records
                </b-badge>
              </div>
            </b-card>

            <!-- Download Section -->
            <b-card
              :class="['mb-4', { 'border-success': downloadCompleted }]"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">Download Records</h6>
                  <small class="text-muted">
                    You must download the records before deletion
                  </small>
                </div>
                
                <div>
                  <b-button
                    v-if="!downloadCompleted"
                    variant="outline-primary"
                    size="sm"
                    :disabled="downloading"
                    @click="downloadRecords"
                  >
                    <feather-icon
                      :icon="downloading ? 'LoaderIcon' : 'DownloadIcon'"
                      class="mr-50"
                      :class="{ 'spin': downloading }"
                    />
                    {{ downloading ? 'Downloading...' : 'Download' }}
                  </b-button>
                  
                  <b-badge
                    v-else
                    variant="success"
                    class="px-2 py-1"
                  >
                    <feather-icon icon="CheckIcon" class="mr-50" />
                    Downloaded
                  </b-badge>
                </div>
              </div>

              <!-- Download Progress -->
              <b-progress
                v-if="downloading"
                height="4px"
                :value="downloadProgress"
                class="mt-2"
              />
            </b-card>

            <!-- Action Buttons - Simplified with SweetAlert -->
            <div class="d-flex justify-content-between mt-4 pt-2">
              <b-button
                variant="outline-secondary"
                @click="closeModal"
              >
                Cancel
              </b-button>
              
              <b-button
                variant="danger"
                :disabled="!downloadCompleted || deleting"
                @click="confirmDeleteWithSwal"
              >
                <feather-icon
                  :icon="deleting ? 'LoaderIcon' : 'TrashIcon'"
                  class="mr-50"
                  :class="{ 'spin': deleting }"
                />
                {{ deleting ? 'Deleting...' : 'Delete Records' }}
              </b-button>
            </div>
          </template>

          <!-- Show message when no records found -->
          <template v-else>
            <b-alert variant="info" show class="mb-4">
              <div class="alert-body text-center">
                <feather-icon icon="InfoIcon" class="mr-50" />
                <strong>No records found</strong>
              </div>
            </b-alert>

            <!-- Only show cancel button when no records -->
            <div class="text-center mt-4">
              <b-button
                variant="outline-secondary"
                @click="closeModal"
              >
                Close
              </b-button>
            </div>
          </template>
        </div>

        <!-- Show when no selection is made yet -->
        <div v-else class="text-center py-4">
          <feather-icon icon="CalendarIcon" size="48" class="text-muted mb-3" />
          <h5 class="text-muted">Select a duration or date range to continue</h5>
          <small class="text-muted">
            Choose which records should be deleted
          </small>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BOverlay,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BDropdown,
  BDropdownItem,
  BModal,
  BButton,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
  BAlert,
  BBadge,
  BProgress,
  BFormText,
  BSpinner,
  BTabs,
  BTab,
  BCardHeader,
  BTfoot,
} from "bootstrap-vue";
import axios from "axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Swal from 'sweetalert2';

export default {
  components: {
    BCard,
    BOverlay,
    BTableSimple,
    BThead,
    BTbody,
    BCardHeader,
    BTr,
    BTh,
    BTd,
    BDropdown,
    BDropdownItem,
    BModal,
    BButton,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BAlert,
    BBadge,
    BProgress,
    BFormText,
    BSpinner,
    BTabs,
    BTab,
    BThead,
    BTfoot,
    DateRangePicker,
  },

  data() {
    return {
      loading: false,
      collections: [],
      selectedCollection: null,
      activeTab: 0,
      selectedDuration: null,
      dateRange: {
        startDate: null,
        endDate: null,
      },
      allCollectionsLoading: false,
      allCollections: [],
      previewCount: 0,
      downloading: false,
      downloadCompleted: false,
      downloadProgress: 0,
      deleting: false,
      fetchingCount: false,
      
      // Static duration options
      staticDurationOptions: [
        { value: '3m', text: 'Older than 3 months' },
        { value: '6m', text: 'Older than 6 months' },
        { value: '9m', text: 'Older than 9 months' },
        { value: '1y', text: 'Older than 1 year' },
        { value: '1.5y', text: 'Older than 1.5 years' },
        { value: '2y', text: 'Older than 2 years' },
        { value: '3y', text: 'Older than 3 years' },
      ],
      
      // Locale data for date picker
      localeData: {
        direction: 'ltr',
        format: 'yyyy-mm-dd',
        separator: ' - ',
        applyLabel: 'Apply',
        cancelLabel: 'Cancel',
        weekLabel: 'W',
        customRangeLabel: 'Custom Range',
        daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthNames: [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ],
        firstDay: 0,
      },
    };
  },

  computed: {
    durationLabel() {
      if (!this.selectedDuration) return '';
      const option = this.staticDurationOptions.find(opt => opt.value === this.selectedDuration);
      return option ? option.text : '';
    },
    
    hasSelection() {
      if (this.activeTab === 0) {
        return this.selectedDuration !== null;
      } else {
        return this.dateRange.startDate !== null && this.dateRange.endDate !== null;
      }
    },
    
    formattedStartDate() {
      if (!this.dateRange.startDate) return '';
      return this.formatDate(this.dateRange.startDate);
    },
    
    formattedEndDate() {
      if (!this.dateRange.endDate) return '';
      return this.formatDate(this.dateRange.endDate);
    },
    
    // For API request
    apiDateRange() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) return null;
      return {
        startDate: this.formatDateForAPI(this.dateRange.startDate),
        endDate: this.formatDateForAPI(this.dateRange.endDate),
      };
    },
    
    // Delete condition text for SweetAlert
    deleteConditionText() {
      if (this.activeTab === 0) {
        return `${this.durationLabel}`;
      } else {
        return `${this.formattedStartDate} to ${this.formattedEndDate}`;
      }
    },

    totalDocuments() {
  return this.allCollections.reduce((sum, item) => sum + (item.count || item.documentCount || 0), 0);
},
totalStorage() {
  return this.allCollections.reduce((sum, item) => sum + (item.storageMB || item.storageSize || 0), 0);
}

  },

  mounted() {
    this.fetchCollections();
      this.fetchAllCollections();
  },

  methods: {
    formatNumber(value, decimals = 0) {
  if (value === null || value === undefined) return '0';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
},
    // Date helper methods
    subtractDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() - days);
      return result;
    },
    
    subtractMonths(date, months) {
      const result = new Date(date);
      result.setMonth(result.getMonth() - months);
      return result;
    },
    
    subtractYears(date, years) {
      const result = new Date(date);
      result.setFullYear(result.getFullYear() - years);
      return result;
    },
    
    startOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    
    endOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    },
    
    startOfYear(date) {
      return new Date(date.getFullYear(), 0, 1);
    },
    
    endOfYear(date) {
      return new Date(date.getFullYear(), 11, 31);
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatDateForAPI(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    },

    async fetchCollections() {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/db/largest-collections`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("_t")}`,
            },
          }
        );

        if (response.data && Array.isArray(response.data)) {
          this.collections = response.data;
          this.showToast("success", "Database collections fetched successfully.");
        } else {
          this.collections = [];
          this.showToast("danger", "Failed to fetch database collections.");
        }
      } catch (err) {
        console.error("Error fetching database stats:", err);
        this.collections = [];
        this.showToast("danger", "Error fetching database stats.");
      } finally {
        this.loading = false;
      }
    },

    async fetchAllCollections() {
  this.allCollectionsLoading = true;
  try {
    const response = await axios.get(
      `${process.env.VUE_APP_API_URL}/db/all-collections`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("_t")}`,
        },
      }
    );

    if (response.data && Array.isArray(response.data)) {
      this.allCollections = response.data;
      // this.showToast("success", "All collections fetched successfully.");
    } else {
      this.allCollections = [];
      this.showToast("danger", "Failed to fetch all collections.");
    }
  } catch (err) {
    console.error("Error fetching all collections:", err);
    this.allCollections = [];
    this.showToast("danger", "Error fetching all collections.");
  } finally {
    this.allCollectionsLoading = false;
  }
},

    openDeleteModal(collection) {
      this.selectedCollection = collection;
      this.resetModalState();
      this.$refs.deleteModal.show();
    },

    closeModal() {
      this.$refs.deleteModal.hide();
    },

    resetModal() {
      this.selectedCollection = null;
      this.resetModalState();
    },

    resetModalState() {
      this.activeTab = 0;
      this.selectedDuration = null;
      this.dateRange = {
        startDate: null,
        endDate: null,
      };
      this.previewCount = 0;
      this.downloading = false;
      this.downloadCompleted = false;
      this.downloadProgress = 0;
      this.deleting = false;
      this.fetchingCount = false;
    },

    async onDurationChange() {
      if (!this.selectedDuration || !this.selectedCollection) return;
      
      // Reset other tab's data
      this.dateRange = { startDate: null, endDate: null };
      
      await this.fetchRecordsPreview();
    },

    onDateRangeChange() {
      if (!this.dateRange.startDate || !this.dateRange.endDate || !this.selectedCollection) return;
      
      // Reset other tab's data
      this.selectedDuration = null;
      
      this.fetchRecordsPreview();
    },

    async fetchRecordsPreview() {
      this.fetchingCount = true;
      this.downloadCompleted = false;
      this.downloading = false;
      this.downloadProgress = 0;
      
      try {
        const requestData = {
          collection: this.selectedCollection.collection,
        };
        
        if (this.activeTab === 0) {
          requestData.duration = this.selectedDuration;
        } else {
          requestData.startDate = this.apiDateRange.startDate;
          requestData.endDate = this.apiDateRange.endDate;
        }
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/db/records-preview`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("_t")}`,
            },
          }
        );
        
        if (response.data.success) {
          this.previewCount = response.data.count || 0;
          
          let message = '';
          if (this.activeTab === 0) {
            message = this.previewCount === 0 
              ? `No records found older than ${this.durationLabel}`
              : `Found ${this.previewCount.toLocaleString()} records older than ${this.durationLabel}`;
          } else {
            message = this.previewCount === 0 
              ? `No records found in the selected date range`
              : `Found ${this.previewCount.toLocaleString()} records in the selected date range`;
          }
          
          this.showToast("success", message);
        } else {
          this.previewCount = 0;
          this.showToast("warning", response.data.message || "Failed to fetch record preview.");
        }
      } catch (error) {
        console.error("Error fetching preview:", error);
        this.previewCount = 0;
        this.showToast("danger", "Failed to fetch record preview.");
      } finally {
        this.fetchingCount = false;
      }
    },

    async downloadRecords() {
      if (!this.selectedCollection || !this.hasSelection) return;
      
      this.downloading = true;
      this.downloadProgress = 0;
      
      try {
        const requestData = {
          collection: this.selectedCollection.collection,
        };
        
        if (this.activeTab === 0) {
          requestData.duration = this.selectedDuration;
        } else {
          requestData.startDate = this.apiDateRange.startDate;
          requestData.endDate = this.apiDateRange.endDate;
        }
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/db/download-records`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("_t")}`,
            },
            responseType: 'blob',
          }
        );
        
        this.downloadProgress = 100;
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        const timestamp = new Date().toISOString().split('T')[0];
        
        if (this.activeTab === 0) {
          link.setAttribute('download', `${this.selectedCollection.collection}_${this.selectedDuration}_${timestamp}.json`);
        } else {
          const startStr = this.apiDateRange.startDate;
          const endStr = this.apiDateRange.endDate;
          link.setAttribute('download', `${this.selectedCollection.collection}_${startStr}_to_${endStr}_${timestamp}.json`);
        }
        
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        this.downloadCompleted = true;
        this.downloading = false;
        this.showToast("success", "Records downloaded successfully.");
        
      } catch (error) {
        console.error("Error downloading records:", error);
        this.downloading = false;
        this.showToast("danger", "Failed to download records.");
      }
    },

    confirmDeleteWithSwal() {
      if (!this.downloadCompleted) {
        this.showToast("warning", "You must download the records before deletion.");
        return;
      }

      Swal.fire({
        title: 'Are you sure?',
        html: `
          <div style="text-align: left;">
            <p style="margin-bottom: 10px;">
              <strong style="color: #dc3545;">⚠️ This action cannot be undone!</strong>
            </p>
            <p style="margin-bottom: 8px;">
              <strong>Collection:</strong> 
              <span style="color: #696cff;">${this.selectedCollection.collection}</span>
            </p>
            <p style="margin-bottom: 8px;">
              <strong>Records to delete:</strong> 
              <span style="color: #dc3545; font-weight: bold;">${this.previewCount.toLocaleString()}</span>
            </p>
            <p style="margin-bottom: 8px;">
              <strong>Condition:</strong> 
              <span style="background: #f8f9fa; padding: 4px 8px; border-radius: 4px;">
                ${this.deleteConditionText}
              </span>
            </p>
            <hr style="margin: 15px 0;">
            <p style="color: #6c757d; font-size: 0.9em;">
              Please confirm that you have downloaded the records before proceeding.
            </p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete them!',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        showLoaderOnConfirm: true,
          customClass: {
    confirmButton: 'btn btn-danger ml-1',
    cancelButton: 'btn btn-secondary'
  },
        preConfirm: () => {
          return this.executeDelete();
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          Swal.fire({
            title: 'Deleted!',
            html: `
              <div style="text-align: center;">
                <p style="margin-bottom: 5px;">
                  Successfully deleted <strong style="color: #28a745;">${result.value.deletedCount?.toLocaleString() || 0}</strong> records.
                </p>
                <p style="color: #6c757d; font-size: 0.9em; margin-top: 10px;">
                  Collection: ${this.selectedCollection.collection}
                </p>
              </div>
            `,
            icon: 'success',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK'
          });
          this.closeModal();
          this.fetchCollections();
        }
      });
    },

    async executeDelete() {
      if (!this.selectedCollection || !this.hasSelection) return;
      
      this.deleting = true;
      
      try {
        const requestData = {
          collection: this.selectedCollection.collection,
        };
        
        if (this.activeTab === 0) {
          requestData.duration = this.selectedDuration;
        } else {
          requestData.startDate = this.apiDateRange.startDate;
          requestData.endDate = this.apiDateRange.endDate;
        }
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/db/delete-records`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("_t")}`,
            },
          }
        );
        
        if (response.data.success) {
          this.showToast("success", `Successfully deleted ${response.data.deletedCount} records.`);
          this.deleting = false;
          return response.data;
        } else {
          throw new Error(response.data.message || "Failed to delete records.");
        }
      } catch (error) {
        console.error("Error deleting records:", error);
        this.showToast("danger", error.response?.data?.message || "Error deleting records.");
        this.deleting = false;
        throw error;
      }
    },

    showToast(variant, message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Notification",
          icon: "BellIcon",
          text: message,
          variant,
        },
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal custom styles */
::v-deep .modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

::v-deep .modal-title {
  color: #2c2e3e;
  font-weight: 600;
}

/* Custom badge styling */
.badge-light-warning {
  background-color: rgba(255, 193, 7, 0.12);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

/* SweetAlert custom styling */
::v-deep .swal2-popup {
  font-family: inherit;
  border-radius: 12px;
  padding: 1.5rem;
}

::v-deep .swal2-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c2e3e;
}

::v-deep .swal2-html-container {
  font-size: 1rem;
  line-height: 1.6;
  margin: 1rem 0;
}

::v-deep .swal2-confirm {
  border-radius: 6px !important;
  padding: 10px 24px !important;
  font-weight: 500 !important;
  transition: all 0.2s;
}

::v-deep .swal2-cancel {
  border-radius: 6px !important;
  padding: 10px 24px !important;
  font-weight: 500 !important;
  transition: all 0.2s;
}

::v-deep .swal2-icon {
  margin: 1rem auto;
}

/* Date range picker styling */
::v-deep .daterangepicker {
  font-family: inherit;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

::v-deep .daterangepicker td.active {
  background-color: #696cff !important;
}

::v-deep .daterangepicker td.in-range {
  background-color: rgba(105, 108, 255, 0.1);
}

/* Tab styling */
::v-deep .nav-tabs .nav-link.active {
  color: #696cff;
  border-color: #696cff #696cff #fff;
  font-weight: 600;
}
</style>